import React from "react";
import { Button, Space, Typography } from "antd";
import { CopyOutlined } from "@ant-design/icons";

const { Text } = Typography;

const parseJsonIfPossible = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    return JSON.parse(trimmed);
  } catch (error) {
    return null;
  }
};

const extractErrorSummary = (error) => {
  if (!error) return "Unknown error";
  const rawMessage = error?.message || String(error);
  const parsed = parseJsonIfPossible(rawMessage);
  const summary =
    parsed?.error?.message ||
    parsed?.error ||
    parsed?.message ||
    parsed?.detail ||
    rawMessage;
  const status = error?.status ? `HTTP ${error.status}` : "";
  return [summary, status].filter(Boolean).join(" • ");
};

export const buildErrorDetails = (error, context) => {
  const time = new Date().toISOString();
  const message = extractErrorSummary(error);
  const stack = error?.stack || "No stack available";
  const status = error?.status ? `HTTP ${error.status}` : "";
  const statusText = error?.statusText || "";
  const url = error?.url || "";
  const method = error?.method || "";
  const action = error?.action || "";
  const payload = error?.payload || null;
  const responseText = error?.responseText || "";
  const parts = [
    `Context: ${context}`,
    `Time: ${time}`,
    `Message: ${message}`,
    status || statusText ? `Status: ${[status, statusText].filter(Boolean).join(" ")}` : "",
    method || url ? `Request: ${[method, url].filter(Boolean).join(" ")}` : "",
    action ? `Action: ${action}` : "",
    payload ? `Payload: ${JSON.stringify(payload, null, 2)}` : "",
    responseText ? `Response: ${responseText}` : "",
    `Stack:\n${stack}`,
  ].filter(Boolean);
  return parts.join("\n");
};

export const showErrorWithCopy = (messageApi, title, error, context) => {
  const details = buildErrorDetails(error, context);
  const summary = extractErrorSummary(error);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(details);
      messageApi.success("Error details copied");
    } catch (copyError) {
      messageApi.error("Copy failed");
    }
  };

  messageApi.open({
    type: "error",
    duration: 6,
    content: (
      <Space direction="vertical" size={2}>
        <Text>{title}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {summary}
        </Text>
        <Button size="small" icon={<CopyOutlined />} onClick={handleCopy}>
          Copy
        </Button>
      </Space>
    ),
  });
};

const CREDITS_PATTERNS = [
  // OpenAI
  { re: /insufficient.quota|exceeded.your.current.quota|billing.hard.limit|you.ve.run.out/i, provider: "OpenAI" },
  // Anthropic / Claude
  { re: /credit.balance|out.of.credits|anthropic.*billing|usage.limit.*reached|402/i, provider: "Anthropic (Claude)" },
  // Google / Gemini
  { re: /RESOURCE_EXHAUSTED|quota.*exceeded|rateLimitExceeded|billing.*not.*enabled|gemini.*quota/i, provider: "Google (Gemini)" },
  // Generic
  { re: /payment.required|quota.exceeded|rate.limit.*credits|billing.*required/i, provider: null },
];

export const detectCreditsError = (error) => {
  const raw = [
    error?.message,
    error?.responseText,
    typeof error === "string" ? error : null,
  ]
    .filter(Boolean)
    .join(" ");

  const parsed = parseJsonIfPossible(raw);
  const combined = [
    raw,
    parsed?.error?.message,
    parsed?.message,
    String(error?.status || ""),
  ]
    .filter(Boolean)
    .join(" ");

  for (const { re, provider } of CREDITS_PATTERNS) {
    if (re.test(combined)) {
      return provider
        ? `You've run out of ${provider} credits. Top up your account to continue.`
        : "API credits exhausted. Check your billing dashboard and top up to continue.";
    }
  }

  // HTTP 402 Payment Required is always a billing issue
  if (Number(error?.status) === 402) {
    return "Payment required — your API credits are exhausted. Top up your account to continue.";
  }

  return null;
};

export const showWorkspaceError = (messageApi, error, context = {}) => {
  const errorData = typeof error === 'object' ? error : { message: error };
  const code = errorData.code || '';
  const message = errorData.message || 'An error occurred';
  const suggestion = errorData.suggestion || '';

  // Workspace-specific error handling
  const workspaceErrors = {
    'WORKSPACE_NOT_MEMBER': {
      message: "You're not a member of this workspace",
      suggestion: "Switch to a workspace you belong to in Settings, or ask the workspace owner to invite you.",
      duration: 8
    },
    'WORKSPACE_INVALID_ACTIVE': {
      message: "Your active workspace is invalid",
      suggestion: "Click the 'Fix Workspace Issues' button to repair it automatically.",
      duration: 10
    },
    'WORKSPACE_NO_WORKSPACES': {
      message: "You don't have any workspaces",
      suggestion: "Create a workspace to get started.",
      duration: 8
    },
    'WORKSPACE_DUPLICATE_MEMBER': {
      message: "This member is already in the workspace",
      suggestion: "Refresh the page to see the latest members.",
      duration: 6
    },
    'WORKSPACE_INVITE_EXISTS': {
      message: "An invite to this email is already pending",
      suggestion: "Check the pending invites section or revoke the existing invite first.",
      duration: 8
    },
    'WORKSPACE_CANNOT_REMOVE_OWNER': {
      message: "Cannot remove the workspace owner",
      suggestion: "Transfer ownership to another member first, then remove them.",
      duration: 8
    },
    'WORKSPACE_OWNER_ONLY': {
      message: "Only workspace owners can assign the owner role",
      suggestion: "Ask a workspace owner to make this change.",
      duration: 8
    }
  };

  const errorConfig = workspaceErrors[code];

  if (errorConfig) {
    messageApi.error({
      content: (
        <div>
          <div style={{ fontWeight: 600 }}>{errorConfig.message}</div>
          {errorConfig.suggestion && (
            <div style={{ fontSize: '0.9em', marginTop: 4, opacity: 0.9 }}>
              {errorConfig.suggestion}
            </div>
          )}
        </div>
      ),
      duration: errorConfig.duration
    });
  } else {
    // Fallback to default error display
    messageApi.error(message + (suggestion ? ` - ${suggestion}` : ''));
  }
};
